import { Icon } from "@iconify/react";
import { Form, Formik } from "formik";
import { useState } from "react";
import { Navigate, useNavigate } from "react-router";
import { getNames } from "country-list";
import { toast } from "react-toastify";
import * as Yup from "yup";
import AlertDialog from "../../components/shared/AlertDialog";
import { Input, Label, TextArea } from "../../components/shared/InputField";
import { Select } from "../../components/shared/Select";
import { SearchableSelect } from "../../components/shared/SearchableSelect";
import { MultiSelectV2 } from "../../components/shared/MultiSelectV2";
import {
  ButtonPrimary,
  ButtonPrimary1,
  Error,
  HStack,
  VStack,
} from "../../components/utils";
import {
  companyClassDropdownValues,
  currencyDropDownValues,
} from "../../constants/DropDownValues";
import { useCreateESOPCompany } from "../../queries/onBoarding";
import { useGetUserCompanies } from "../../queries";
import BCHeader from "../../shared/BCHeader";
import { useIsInternalUser } from "../../utils/useIsInternalUser";

function CreateCompany() {
  const navigate = useNavigate();
  const [dialog, setDialog] = useState<{
    open: boolean;
    message?: string;
  }>({
    open: false,
  });
  const { mutate: createEsopCompany, isLoading } = useCreateESOPCompany();
  const { data: userCompanies } = useGetUserCompanies();
  const isRuleZeroUser = useIsInternalUser();
  const companyOptions = (userCompanies?.associatedCompanies || []).map(
    (company) => ({ label: company.name, value: company.id })
  );
  const discardCompany = () => {
    setDialog({
      open: true,
      message: "Do you want to discard the company?",
    });
  };
  const validationCompanyDetailsSchema = Yup.object().shape({
    name: Yup.string().required("Company Name is required"),
    cin: Yup.string().required("CIN Number is required"),
    address: Yup.string().label("Address is required"),
    dateOfIncorporation: Yup.string().required(
      "Date of Incorporation is required"
    ),
    email: Yup.string()
      .email("Enter Valid Company Email")
      .required("Email is required"),
    companyCurrency: Yup.string().required("Company Currency is required"),
    registeredCountry: Yup.string().required("Registered country is required"),
    nic: Yup.string().required("NIC is required"),
    operatingAs: Yup.string().required("Doing Business As is required"),
    typeOfTheCompany: Yup.string().label("Class of the company"),
    stockSymbol: Yup.string().label("Stock Symbol"),
  });
  function getInitialValue(): any {
    return {
      name: "",
      cin: "",
      address: "",
      dateOfIncorporation: "",
      email: "",
      companyCurrency: "",
      registeredCountry: "",
      nic: "",
      operatingAs: "",
      typeOfTheCompany: "Private",
      stockSymbol: undefined,
      parentCompanyId: "",
      subsidiaryCompanyIds: [],
    };
  }

  if (!isRuleZeroUser) return <Navigate to="/404" replace />;

  return (
    <main className="min-h-screen bg-slate-light px-4 py-6 md:px-6">
      <VStack className="mx-auto w-full max-w-4xl gap-6">
        <VStack className="gap-1">
          <h1 className="text-xl font-semibold leading-7 text-headerColor">
            Create company
          </h1>
          <p className="text-sm leading-5 text-secondaryText">
            Enter the company details to begin ESOP onboarding.
          </p>
        </VStack>
        <BCHeader bcTitle="Onboarding" bcSubTitle="Company details" />
        <Formik
          enableReinitialize={true}
          initialValues={getInitialValue()}
          onSubmit={(values) => {
            createEsopCompany(values, {
              onSuccess: () => {
                toast.success("Company created successfully");
                setTimeout(() => {
                  navigate("/options/esopOverview");
                }, 1000);
              },
              onError: () => {
                toast.error("We couldn't create the company");
              },
            });
          }}
          validationSchema={validationCompanyDetailsSchema}
        >
          {(formik) => (
            <>
              <Form>
                <AlertDialog
                  onClose={() => setDialog({ open: false })}
                  open={dialog.open}
                  message={dialog.message}
                  primaryActionText="Discard"
                  onPrimaryAction={() => navigate("/options/esopOverview")}
                  onSecondaryAction={() => setDialog({ open: false })}
                />
                <VStack className="w-full gap-6 rounded-lg border border-borderColor bg-white p-6 shadow-card">
                  <VStack className="gap-2">
                    <HStack className="flex-col gap-4 md:flex-row">
                      <div className="flex-1">
                        <Label className="text-sm font-normal">
                          Name of the Company
                          <span className="text-rose-400">*</span>
                        </Label>
                        <Input
                          type="text"
                          placeholder="Enter Company Name"
                          className=""
                          {...formik.getFieldProps("name")}
                        />
                        {formik.touched.name && formik.errors.name && (
                          <Error text={formik.errors.name?.toString()} />
                        )}
                      </div>
                      <div className="flex-1">
                        <Label className="text-sm font-normal">
                          CIN Number
                          <span className="text-rose-400">*</span>
                        </Label>
                        <Input
                          type="text"
                          placeholder="Eg:38928432"
                          className=""
                          {...formik.getFieldProps("cin")}
                        />
                        {formik.touched.cin && formik.errors.cin && (
                          <Error text={formik.errors.cin?.toString()} />
                        )}
                      </div>
                    </HStack>
                  </VStack>
                  <HStack className="flex-col gap-4 md:flex-row">
                    <div className="flex-1">
                      <Label className="text-sm font-normal">
                        Company Email ID
                        <span className="text-rose-400">*</span>
                      </Label>
                      <Input
                        type="text"
                        placeholder="Eg: example@domain.com"
                        {...formik.getFieldProps("email")}
                      />
                      {formik.touched.email && formik.errors.email && (
                        <Error text={formik.errors.email?.toString()} />
                      )}
                    </div>
                    <div className="flex-1">
                      <Label className="text-sm font-normal">
                        Date of Incorporation
                        <span className="text-rose-400">*</span>
                      </Label>
                      <Input
                        type="date"
                        placeholder="Eg:12-12-1212"
                        {...formik.getFieldProps("dateOfIncorporation")}
                      />
                      {formik.touched.dateOfIncorporation &&
                        formik.errors.dateOfIncorporation && (
                          <Error
                            text={formik.errors.dateOfIncorporation?.toString()}
                          />
                        )}
                    </div>
                  </HStack>
                  <HStack className="flex-col gap-4 md:flex-row">
                    <div className="flex-1">
                      <Label className="text-sm font-normal">
                        Corporate Office Address
                      </Label>
                      <TextArea
                        placeholder="Enter Your Company Address"
                        {...formik.getFieldProps("address")}
                      />
                    </div>
                    <div className="flex-1">
                      <Label className="text-sm font-normal">
                        Company Currency
                        <span className="text-rose-400">*</span>
                      </Label>
                      <Select
                        options={currencyDropDownValues}
                        isPlaceHolderDisabled={true}
                        {...formik.getFieldProps("companyCurrency")}
                      />
                      {formik.touched.companyCurrency &&
                        formik.errors.companyCurrency && (
                          <Error
                            text={formik.errors.companyCurrency?.toString()}
                          />
                        )}
                    </div>
                  </HStack>
                  <HStack className="flex-col gap-4 md:flex-row">
                    <div className="flex-1">
                      <Label className="text-sm font-normal">
                        NIC
                        <span className="text-rose-400">*</span>
                      </Label>
                      <Input type="text" {...formik.getFieldProps("nic")} />
                      {formik.touched.nic && formik.errors.nic && (
                        <Error text={formik.errors.nic?.toString()} />
                      )}
                    </div>
                    <div className="flex-1">
                      <Label className="text-sm font-normal">
                        Doing Business As
                        <span className="text-rose-400">*</span>
                      </Label>
                      <Input
                        type="text"
                        {...formik.getFieldProps("operatingAs")}
                      />
                      {formik.touched.operatingAs &&
                        formik.errors.operatingAs && (
                          <Error text={formik.errors.operatingAs?.toString()} />
                        )}
                    </div>
                  </HStack>
                  <HStack className="flex-col gap-4 md:flex-row">
                    <div className="flex-1">
                      <Label className="text-sm font-normal">
                        Class (Type Of The Company)
                        <span className="text-rose-400">*</span>
                      </Label>
                      <Select
                        options={companyClassDropdownValues}
                        valueGetter={(value) => value.value}
                        textGetter={(value) => value.label}
                        isPlaceHolderDisabled={true}
                        {...formik.getFieldProps("typeOfTheCompany")}
                      />
                      {formik.touched.typeOfTheCompany &&
                        formik.errors.typeOfTheCompany && (
                          <Error
                            text={formik.errors.typeOfTheCompany?.toString()}
                          />
                        )}
                    </div>
                    <div className="flex-1">
                      {formik.values.typeOfTheCompany === "PUBLIC" && (
                        <HStack className="flex-col gap-4 md:flex-row">
                          <div className="flex-1">
                            <Label className="text-sm font-normal">
                              Stock Symbol
                            </Label>
                            <Input
                              type="text"
                              {...formik.getFieldProps("stockSymbol")}
                            />
                            {formik.touched.stockSymbol &&
                              formik.errors.stockSymbol && (
                                <Error
                                  text={formik.errors.stockSymbol?.toString()}
                                />
                              )}
                          </div>
                          <div className="flex-1" />
                        </HStack>
                      )}
                    </div>
                  </HStack>
                  <HStack className="flex-col gap-4 md:flex-row">
                    <div className="flex-1">
                      <Label className="text-sm font-normal">
                        Parent company
                      </Label>
                      <SearchableSelect
                        options={companyOptions}
                        placeholder="Select a parent company"
                        valueGetter={(option) => option.value}
                        textGetter={(option) => option.label}
                        value={formik.values.parentCompanyId}
                        onChange={(event) =>
                          formik.setFieldValue(
                            "parentCompanyId",
                            event.target.value
                          )
                        }
                      />
                    </div>
                    <div className="flex-1">
                      <Label className="text-sm font-normal">
                        Subsidiary companies
                      </Label>
                      <MultiSelectV2
                        multiple
                        searchable
                        placeholder="Select subsidiary companies"
                        options={companyOptions.filter(
                          (company) =>
                            company.value !== formik.values.parentCompanyId
                        )}
                        optionValues={formik.values.subsidiaryCompanyIds}
                        handleSelection={(values) =>
                          formik.setFieldValue("subsidiaryCompanyIds", values)
                        }
                      />
                    </div>
                  </HStack>
                  <HStack
                    className="items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800"
                    role="alert"
                  >
                    <Icon
                      icon="fe:warning"
                      width={18}
                      className="mt-0.5 shrink-0"
                    />
                    <p>
                      Selected companies will be linked as subsidiaries after
                      creation. This action cannot be undone once the subsidiary
                      has ESOP data.
                    </p>
                  </HStack>
                  <HStack className="flex-col gap-4 md:flex-row">
                    <div className="flex-1">
                      <Label className="text-sm font-normal">
                        Registered country
                        <span className="text-rose-400">*</span>
                      </Label>
                      <Select
                        options={getNames()}
                        isPlaceHolderDisabled={true}
                        placeholder="Select registered country"
                        {...formik.getFieldProps("registeredCountry")}
                      />
                      {formik.touched.registeredCountry &&
                        formik.errors.registeredCountry && (
                          <Error
                            text={formik.errors.registeredCountry.toString()}
                          />
                        )}
                      <HStack className="mt-2 items-start gap-1.5 text-xs text-slate-600">
                        <Icon
                          icon="mdi:information-outline"
                          width={16}
                          className="mt-px shrink-0 text-primary"
                        />
                        <p>
                          Grant parameters restrictions will be based on the
                          selected country.
                        </p>
                      </HStack>
                    </div>
                    <div className="flex-1" />
                  </HStack>
                </VStack>
                <HStack className="w-full flex-col-reverse gap-3 border-t border-borderColor pt-4 sm:flex-row sm:justify-end">
                  <ButtonPrimary1
                    type="button"
                    className="justify-center"
                    onClick={() => {
                      navigate("/options/esopOverview");
                    }}
                  >
                    Go to Dashboard
                  </ButtonPrimary1>
                  <ButtonPrimary1
                    type="button"
                    className="justify-center"
                    onClick={discardCompany}
                  >
                    Discard
                  </ButtonPrimary1>
                  <ButtonPrimary
                    type="submit"
                    className="justify-center"
                    loading={isLoading}
                  >
                    Continue
                  </ButtonPrimary>
                </HStack>
              </Form>
            </>
          )}
        </Formik>
      </VStack>
    </main>
  );
}

export default CreateCompany;
